/**
 * The reported rates behind the time estimates' priors (DESIGN UD-11). Task durations are measured and reported, not gated
 * (`test/perf/results/durations-*.json`); these constants are those measurements, rounded, on the reference machine
 * (MacBook Pro M1 Pro, 2026-10-04): analysis of 500k words takes about 4.0 s in Node and 4.9 s in Chromium (ingest, analysis and
 * first paint); the bench's baseline, inventory and chunk plan 1.6 s at 100k and 8.2 s at 500k words (Node, warm engine); the
 * local pass itself 0.17 s at 500k plus two analyses (3.2 s at 100k, 8.1 s at 500k in all).
 *
 * A slower machine is handled two ways: the live extrapolation (`estimate.ts`) takes over from the prior as progress comes in,
 * and the rate a finished analysis measured on this machine is kept (`localStorage`, best effort) and scales every prior
 * afterwards. Nothing here gates anything.
 */
export const REFERENCE = {
	/** Chromium ingest + analysis + first paint, per word, plus a fixed part for the worker start. */
	analysisFixedMs: 300,
	analysisMsPerWord: 0.0092,
	/** The bench worker's baseline, inventory and chunk plan (a cold run). */
	prepareFixedMs: 100,
	prepareMsPerWord: 0.0162,
	/** The local pass: one pass over the text and two analyses of it (before and after). */
	localPassMsPerWord: 0.0004,
} as const;

const KEY = 'provenance.analysisMsPerWord';
/** Bounds on how far this machine may differ from the reference before the figure is ignored as noise. */
const MIN_FACTOR = 0.5;
const MAX_FACTOR = 12;

function load(): number | null {
	try {
		const v = Number(localStorage.getItem(KEY));
		return Number.isFinite(v) && v > 0 ? v : null;
	} catch {
		return null;
	}
}

/** Keeps the analysis rate this machine just measured (ms per word, whole run). Ignores runs too short to mean anything. */
export function recordAnalysisRate(words: number, ms: number): void {
	if (words < 20_000 || ms <= 0) return;
	try {
		localStorage.setItem(KEY, String(ms / words));
	} catch {
		/* storage unavailable: the priors stay at the reference */
	}
}

/** How much slower (above 1) or faster this machine is than the reference, from the last analysis it ran. */
export function machineFactor(): number {
	const r = load();
	if (r === null) return 1;
	return Math.max(MIN_FACTOR, Math.min(MAX_FACTOR, r / REFERENCE.analysisMsPerWord));
}

export const analysisPriorMs = (words: number): number =>
	(REFERENCE.analysisFixedMs + REFERENCE.analysisMsPerWord * words) * machineFactor();

export const preparePriorMs = (words: number): number =>
	(REFERENCE.prepareFixedMs + REFERENCE.prepareMsPerWord * words) * machineFactor();

/** The local pass: analyse, transform, analyse again. */
export const localPassPriorMs = (words: number): number =>
	(2 * (REFERENCE.analysisMsPerWord * words) + REFERENCE.localPassMsPerWord * words) *
	machineFactor();
